import { camelToProperCase, is, obj } from '@easeltv/utilities';
import { FormEvent, useReducer, useState } from 'react';
import { ZodType } from 'zod';

import { useDictionary } from '../../../hooks/useDictionary/useDictionary';
import { Field } from '../../../providers/RegistrationProvider/RegistrationProvider.helpers';
import FormBlockDumb from './FormBlock.dumb';
import {
    FormActionTypes,
    formReducer,
    initializeFormState,
    removeEmptyStringFromObject,
} from './FormBlock.helpers';

/* ---------------------------------- Types --------------------------------- */

export type SetErrorFn = (id: string) => (value: string) => void;

interface FormBlockI<T> {
    labels: {
        submitLabel: string;
        disclaimer?: {
            text: string;
            label: string;
            url: string;
        };
    };
    handleOnSubmit: (data: T, setError: SetErrorFn) => void;
    validator: ZodType<T>;
    fields: Field[];
    forgotPassword?: boolean;
}

/* -------------------------------- Component ------------------------------- */

const FormBlock = <T,>({
    fields,
    labels,
    handleOnSubmit: submit,
    validator,
    forgotPassword = false,
}: FormBlockI<T>) => {
    const forgotPasswordLabel = useDictionary('login.forgot-password.label');

    const [isSubmitting, setIsSubmitting] = useState(false);

    // Reducer that controls the form state
    const [state, dispatch] = useReducer(
        formReducer,
        fields,
        initializeFormState,
    );

    // Convenience method for updating field values
    const setError: SetErrorFn = (id) => (value) => {
        dispatch({
            type: FormActionTypes.SetError,
            payload: {
                id,
                value,
            },
        });

        setIsSubmitting(false);
    };

    // Convenience method for updating field values
    const updateField = (id: string) => (value: unknown) => {
        setError(id)('');

        dispatch({
            type: FormActionTypes.UpdateField,
            payload: {
                id,
                value,
            },
        });
    };

    const handleOnSubmit = (e: FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);

        const sanitizedFields = removeEmptyStringFromObject(state.fields);

        const result = validator.safeParse(sanitizedFields);

        if (!result.success) {
            setIsSubmitting(false);
            const { fieldErrors } = result.error.flatten((i) => {
                const field = camelToProperCase(i.path.join('.'));
                return i.message.replace('{0}', field);
            });

            obj.entries(fieldErrors)
                .filter(is.notUndefined)
                .forEach((entry) => {
                    const [key, value] = entry ?? [];
                    if (key && value && value.length > 0) {
                        setError(String(key))(value[0]);
                    }
                });
        } else {
            submit(result.data, setError);
        }
    };

    return (
        <FormBlockDumb
            labels={labels}
            fields={fields}
            handleOnSubmit={handleOnSubmit}
            state={state}
            updateField={updateField}
            forgotPassword={forgotPassword ? forgotPasswordLabel : undefined}
            isSubmitting={isSubmitting}
        />
    );
};

/* --------------------------------- Export --------------------------------- */

export default FormBlock;
